import React, { forwardRef } from 'react'
import { styled, Grid as GridBase, Divider as DividerBase } from '@mui/material'
import {
  CheckBox as CheckboxBase,
  Button as BaseButton,
  DataTable as BaseDataTable,
  Card as BaseCard,
  Tooltip as TooltipBase
} from '@chilecompra/react-kit'
import { Search as SearchIconIconBaseIcon, Error, Help as HelpIcon } from '@mui/icons-material'

export const GridContainer = styled(props => <GridBase {...props} container />)`
  &.MuiGrid-container {
    margin: ${props => props.margin || '0'};
    padding: ${props => props.padding || '0'};
  }
`

export const GridItem = styled(props => <GridBase {...props} item />)`
  &.MuiGrid-item {
    display: flex;
    flex-direction: column;
    margin: ${props => props.margin || '0'};
    padding: ${props => props.padding || '0'};
  }
`

export const DescriptionTitle = styled('div')`
  color: ${props => props.theme.palette.background.black1};
  font-size: 27px;
  font-style: normal;
  font-weight: 700;
  line-height: 31px;
  padding-top: 8px;
  ${props => props.theme.breakpoints.down('md')} {
    font-size: 22px;
    line-height: 26px;
  }
`

export const DescriptionText = styled(DescriptionTitle)`
  color: ${props => props.theme.palette.brand.black1};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  padding: 15px 0 24px;
  text-wrap: balance;
  ${props => props.theme.breakpoints.down('lg')} {
    text-wrap: pretty;
  }
`

export const ProcessId = styled('span')`
  color: ${props => props.theme.palette.brand.primary};
  font-size: 27px;
  font-weight: 700;
  line-height: 31px;
  text-align: left;
`

export const ElementItem = styled(GridItem)`
  &.MuiGrid-item {
    border: 1px solid ${props => props.theme.palette.brand.gray2};
    border-radius: 3px;
    padding: ${props => props.padding || '44px'};
    ${props => props.theme.breakpoints.down('md')} {
      padding: ${props => props.padding || '17px'};
    }
  }
`

export const Divider = styled(({ ...props }) => <DividerBase {...props} />)`
  &.MuiDivider-root {
    align-items: center;
    color: ${props => props.theme.palette.brand.gray2};
    display: flex;
    flex-shrink: 0;
    height: 1px;
    justify-content: center;
    margin-top: 15px;
    margin-bottom: 15px;
  }
`

export const CheckBox = styled(CheckboxBase)`
  &.MuiFormControlLabel-root .MuiFormControlLabel-label {
    margin: 0;
    padding: ${props => props.padding || '0 0 20px'};
  }
`

export const DataTableContainer = styled('div')`
  && .MuiTableRow-root .MuiTableCell-root:first-of-type {
    padding: 8px 8px 8px 24px;
  }
  ${props => props.theme.breakpoints.up('md')} {
    min-height: 205px;
  }
`

export const TableCheckBox = styled(CheckboxBase)`
  && span {
    margin: 0;
    padding: 0;
  }
`

export const ButtonContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`

export const SearchButton = styled(props => <BaseButton {...props} />)`
  &.MuiButtonBase-root {
    max-width: 45px;
    width: 45px;
    border-radius: 0rem;
    padding: 0px;
  }
`

export const SearchIcon = styled(props => <SearchIconIconBaseIcon {...props} item />)`
  font-size: 20px;
`

export const ErrorContainer = styled(props => <GridBase {...props} />)`
  border: ${props => props.border || `1px solid ${props.theme.palette.brand.gray2}`};
  height: 243px;
  margin: 30px 0 0;
  padding: ${props => props.padding || '0'};
  width: 100%;
`

export const ErrorMessageContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  max-width: 600px;
`

export const ErrorIcon = styled(Error)`
  &.MuiSvgIcon-root {
    color: ${props => props.theme.palette.gray[85]};
    height: 70px;
    margin: 0 auto;
    width: 70px;
  }
`

export const AlertIcon = styled('img')`
  height: 48px;
  margin: 0 auto;
  width: 48px;
  ${props => props.theme.breakpoints.down('md')} {
    padding: 0 0 0 15px;
  }
`

export const NoDataIcon = styled('img')`
  width: 65px;
  height: 55px;
`

export const RootNotAllowed = styled('div')`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  margin: 0;
  min-height: 59vh;
  padding: 0;
  width: 100%;

  ${props => props.theme.breakpoints.down('lg')} {
    min-height: 59vh;
  }
`

export const Text = styled('h3')`
  color: ${props => props.theme.palette.brand.black1};
  font-size: 22px;
  font-weight: 300;
  margin: 0;
`

export const Image404 = styled('img')`
  width: 50%;
`

export const Card = styled(BaseCard)`
  &.MuiPaper-root {
    display: flex;

    &.not__found {
      align-items: center;
      justify-content: center;
      min-height: 300px;

      .MuiGrid-container {
        text-align: center;
      }
    }
  }
`

export const NotFoundImg = styled('img')`
  color: ${props => props.theme.palette.gray[50]};
  height: auto;
  pointer-events: none;
  user-select: none;
  width: 64px;
`

export const Tooltip = styled(TooltipBase)`
  &.MuiTooltip-popper .MuiTooltip-tooltip {
    max-height: 100%;
    max-width: 30ch;

    > div {
      line-height: 1.4;
      max-height: 100%;
      word-break: auto-phrase;
    }
  }
`

export const TooltipIcon = styled(forwardRef(({ size, ...props }, ref) => <HelpIcon {...props} ref={ref} />))`
  &.MuiSvgIcon-root {
    fill: ${props => props.theme.palette.brand.black2};
    width: 18px;
    height: 18px;
    margin-left: 6px;
  }
`

export const TooltipText = styled('span')`
  margin: 0;
`
